/**
 * FIRE PIT (2026-10-19, Matt: "Make a fire pit family like this that has length and width
 * as instance parameters"): a rectangular gas fire pit built the way the firm details one —
 * a STONE body (stone to match the house), a 3" POLISHED CONCRETE CAP that overhangs the
 * stone 1" all round and frames the burner opening, and FIRE MEDIA heaped in the opening
 * (fire balls in two courses, a bed of fire glass, or none).
 *
 * Local frame (the object convention): x across the length centred on 0, y from the anchor
 * (y = 0) to the far side, z up from the ground. The overall size IS the cap (the 5'-6"
 * dimension runs cap edge to cap edge; the height is the top of the cap).
 *
 * Each part carries its own material through `solidGroups`: the picked stone / cap material,
 * else a neutral stone and a buff concrete. The balls are smooth spheres with no feature
 * edges; an elevation draws each ball's outline from the view (section.ts's silhouettes of
 * curved surfaces). Fixed outline circles were tried the first day and dropped — the one
 * across the view drew a vertical line through every ball.
 */
import { MeshBuilder } from '../../extrude'
import { featureEdges } from '../../edges'
import { tintMaterialId, FIRE_PIT_CAP_IN, FIRE_PIT_CAP_OVERHANG_IN, FIRE_PIT_CAP_BAND_IN, type Pt2 } from '@aj/bim-kernel'
import type { SolidGroup } from '../opening/protos'

const STONE = '#b3aa98'
const CAP = '#d9cdb0'         // a Miami-buff concrete
const PAN = '#3a3936'
const BALL = '#8d8a84'
const GLASS = '#5d7f86'

/** a fire ball's diameter (feet) — the 4" ceramic ball */
const BALL_FT = 4 / 12
/** the most balls one pit builds (a very large pit grows the balls instead) */
const MAX_BALLS = 360

export interface FirePitParts {
  /** the burner opening in the cap, local feet */
  opening: { x0: number; x1: number; y0: number; y1: number }
  /** the stone body's outline (inside the cap overhang) */
  stone: { x0: number; x1: number; y0: number; y1: number }
  /** each ball's centre and radius (plan circles + 3D spheres) */
  balls: { x: number; y: number; z: number; r: number }[]
  capZ0: number
}

/** the fire pit's layout — shared by the 3D body and the plan symbol so they always agree */
export function firePitLayout(w: number, d: number, h: number, media: string): FirePitParts {
  const capT = Math.min(FIRE_PIT_CAP_IN / 12, h * 0.4)
  const ov = FIRE_PIT_CAP_OVERHANG_IN / 12
  // the cap band: the usual 10", narrowed on a small pit so the opening stays at least 6"
  const band = Math.max(0.25, Math.min(FIRE_PIT_CAP_BAND_IN / 12, (w - 0.5) / 2, (d - 0.5) / 2))
  const opening = { x0: -w / 2 + band, x1: w / 2 - band, y0: band, y1: d - band }
  const stone = { x0: -w / 2 + ov, x1: w / 2 - ov, y0: ov, y1: d - ov }
  const capZ0 = h - capT
  const balls: FirePitParts['balls'] = []
  if (media === 'balls') {
    const ow = opening.x1 - opening.x0, od = opening.y1 - opening.y0
    // grow the ball on a very large pit so the count stays bounded
    let dia = BALL_FT
    const count = (dd: number) => Math.floor(ow / dd) * Math.floor(od / dd)
    while (count(dia) * 1.8 > MAX_BALLS) dia *= 1.15
    const r = dia / 2
    // the first course sits on the burner pan (at the cap's underside), packed edge to edge
    const course = (n: number, m: number, x0: number, y0: number, z: number) => {
      for (let i = 0; i < n; i++) for (let j = 0; j < m; j++) balls.push({ x: x0 + r + i * dia, y: y0 + r + j * dia, z, r })
    }
    const n1 = Math.max(1, Math.floor(ow / dia)), m1 = Math.max(1, Math.floor(od / dia))
    const x1 = -(n1 * dia) / 2, y1 = opening.y0 + (od - m1 * dia) / 2
    const z1 = capZ0 + r
    course(n1, m1, x1, y1, z1)
    // the second course nests in the hollows of the first (the mound the detail shows)
    if (n1 > 1 && m1 > 1) course(n1 - 1, m1 - 1, x1 + r, y1 + r, z1 + r * Math.SQRT2)
    else if (n1 > 1) course(n1 - 1, 1, x1 + r, y1, z1 + r * Math.sqrt(3))
  }
  return { opening, stone, balls, capZ0 }
}

/** a UV sphere, smooth normals — 16 round × 10 up keeps every crease under the 25°
 *  feature-edge threshold, so the ball draws no lines of its own */
function sphere(m: MeshBuilder, cx: number, cy: number, cz: number, r: number, seg = 16, rings = 10) {
  const pt = (i: number, j: number): [number[], number[]] => {
    const th = (Math.PI * j) / rings, ph = (Math.PI * 2 * i) / seg
    const n = [Math.sin(th) * Math.cos(ph), Math.sin(th) * Math.sin(ph), Math.cos(th)]
    return [[cx + n[0] * r, cy + n[1] * r, cz + n[2] * r], n]
  }
  for (let j = 0; j < rings; j++) {
    for (let i = 0; i < seg; i++) {
      const [a, na] = pt(i, j), [b, nb] = pt(i + 1, j), [c, nc] = pt(i + 1, j + 1), [e, ne] = pt(i, j + 1)
      if (j > 0) m.addTriN(a, e, c, na, ne, nc)
      if (j < rings - 1) m.addTriN(a, c, b, na, nc, nb)
    }
  }
}

function boxInto(m: MeshBuilder, x0: number, x1: number, y0: number, y1: number, zA: number, zB: number) {
  if (x1 - x0 < 1e-4 || y1 - y0 < 1e-4 || zB - zA < 1e-4) return
  const poly: Pt2[] = [[x0, y0], [x1, y0], [x1, y1], [x0, y1]]
  const bot = poly.map((q) => [q[0], q[1], zA]), top = poly.map((q) => [q[0], q[1], zB])
  for (let i = 2; i < 4; i++) {
    m.addTri(top[0], top[i - 1], top[i], [0, 0, 1])
    m.addTri(bot[0], bot[i], bot[i - 1], [0, 0, -1])
  }
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4
    const nx = poly[j][1] - poly[i][1], ny = poly[i][0] - poly[j][0], l = Math.hypot(nx, ny) || 1
    const nn = [nx / l, ny / l, 0]
    m.addTri(bot[i], bot[j], top[j], nn)
    m.addTri(bot[i], top[j], top[i], nn)
  }
}

/** the fire-glass bed: a low hipped mound filling the opening */
function glassBed(m: MeshBuilder, o: FirePitParts['opening'], z0: number, rise: number) {
  const inset = Math.min((o.x1 - o.x0), (o.y1 - o.y0)) * 0.3
  const a: number[][] = [[o.x0, o.y0, z0], [o.x1, o.y0, z0], [o.x1, o.y1, z0], [o.x0, o.y1, z0]]
  const t: number[][] = [[o.x0 + inset, o.y0 + inset, z0 + rise], [o.x1 - inset, o.y0 + inset, z0 + rise], [o.x1 - inset, o.y1 - inset, z0 + rise], [o.x0 + inset, o.y1 - inset, z0 + rise]]
  const nrm = (p: number[], q: number[], s: number[]) => {
    const u = [q[0] - p[0], q[1] - p[1], q[2] - p[2]], v = [s[0] - p[0], s[1] - p[1], s[2] - p[2]]
    const n = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]]
    const l = Math.hypot(n[0], n[1], n[2]) || 1
    return [n[0] / l, n[1] / l, n[2] / l]
  }
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4
    const n = nrm(a[i], a[j], t[j])
    m.addTri(a[i], a[j], t[j], n)
    m.addTri(a[i], t[j], t[i], n)
  }
  m.addTri(t[0], t[1], t[2], [0, 0, 1])
  m.addTri(t[0], t[2], t[3], [0, 0, 1])
}

/** build the fire pit into `b` (the proto's one solid); returns the per-part materials and
 *  the edge set (the hard parts' feature edges) */
export function buildFirePit(
  w: number, d: number, h: number, b: MeshBuilder,
  opts: { media: string; stoneMaterial: string | null; capMaterial: string | null },
): { groups: SolidGroup[]; edges: Float32Array } {
  const L = firePitLayout(w, d, h, opts.media)
  const o = L.opening, s = L.stone
  const stone = new MeshBuilder(), cap = new MeshBuilder(), pan = new MeshBuilder(), media = new MeshBuilder()
  // the stone body, up to the cap's underside
  boxInto(stone, s.x0, s.x1, s.y0, s.y1, 0, L.capZ0)
  // the cap: a frame round the burner opening
  boxInto(cap, -w / 2, w / 2, 0, o.y0, L.capZ0, h)
  boxInto(cap, -w / 2, w / 2, o.y1, d, L.capZ0, h)
  boxInto(cap, -w / 2, o.x0, o.y0, o.y1, L.capZ0, h)
  boxInto(cap, o.x1, w / 2, o.y0, o.y1, L.capZ0, h)
  // the burner pan, flush with the cap's underside
  boxInto(pan, o.x0, o.x1, o.y0, o.y1, L.capZ0 - 0.02, L.capZ0)
  if (opts.media === 'balls') {
    for (const bl of L.balls) sphere(media, bl.x, bl.y, bl.z, bl.r)
  } else if (opts.media === 'glass') {
    glassBed(media, o, L.capZ0, Math.min(0.3, (h - L.capZ0) + 0.08))
  }

  const groups: SolidGroup[] = []
  const append = (m: MeshBuilder, materialId: string) => {
    if (!m.vertCount) return
    const start = b.idx.length, base = b.vertCount
    for (let i = 0; i < m.pos.length; i++) b.pos.push(m.pos[i])
    for (let i = 0; i < m.nrm.length; i++) b.nrm.push(m.nrm[i])
    for (const i of m.idx) b.idx.push(i + base)
    b.vertCount += m.vertCount
    groups.push({ start, count: m.idx.length, materialId })
  }
  append(stone, opts.stoneMaterial || tintMaterialId(STONE, 'default'))
  append(cap, opts.capMaterial || tintMaterialId(CAP, 'default'))
  append(pan, tintMaterialId(PAN, 'default'))
  append(media, tintMaterialId(opts.media === 'glass' ? GLASS : BALL, 'default'))

  // feature edges of the hard parts only (a sphere's smooth faces would add nothing)
  const hard = new MeshBuilder()
  for (const m of [stone, cap, pan, ...(opts.media === 'glass' ? [media] : [])]) {
    const base = hard.vertCount
    for (let i = 0; i < m.pos.length; i++) hard.pos.push(m.pos[i])
    for (let i = 0; i < m.nrm.length; i++) hard.nrm.push(m.nrm[i])
    for (const i of m.idx) hard.idx.push(i + base)
    hard.vertCount += m.vertCount
  }
  return { groups, edges: hard.vertCount ? featureEdges(hard.build()) : new Float32Array(0) }
}

/** the fire pit in plan: the cap, the burner opening, the stone face under the cap overhang
 *  (dashed), and the media — each fire ball's circle, or the glass bed's hip lines */
export function firePitPlanSymbol(w: number, d: number, h: number, media: string): { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] {
  const L = firePitLayout(w, d, h, media)
  const o = L.opening, s = L.stone
  const rect = (x0: number, y0: number, x1: number, y1: number, dashed = false) =>
    ({ pts: [[x0, y0], [x1, y0], [x1, y1], [x0, y1]] as Pt2[], closed: true, ...(dashed ? { dashed } : {}) })
  const out: { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] = [
    rect(-w / 2, 0, w / 2, d),
    rect(o.x0, o.y0, o.x1, o.y1),
    rect(s.x0, s.y0, s.x1, s.y1, true),
  ]
  if (media === 'balls') {
    // the first course only — the packed bed, read from above
    const z0 = Math.min(...L.balls.map((b) => b.z))
    for (const bl of L.balls) {
      if (bl.z > z0 + 1e-6) continue
      const pts: Pt2[] = []
      for (let i = 0; i < 16; i++) { const a = (Math.PI * 2 * i) / 16; pts.push([bl.x + Math.cos(a) * bl.r, bl.y + Math.sin(a) * bl.r]) }
      out.push({ pts, closed: true })
    }
  } else if (media === 'glass') {
    const inset = Math.min(o.x1 - o.x0, o.y1 - o.y0) * 0.3
    const t = rect(o.x0 + inset, o.y0 + inset, o.x1 - inset, o.y1 - inset)
    out.push(t)
    const c: Pt2[] = [[o.x0, o.y0], [o.x1, o.y0], [o.x1, o.y1], [o.x0, o.y1]]
    for (let i = 0; i < 4; i++) out.push({ pts: [c[i], t.pts[i]] })
  }
  return out
}
